import {
    formatDayOfWeekMonthDay,
    getClientTimeZoneLabel,
    getErrorMessage,
} from '@fruitful/fruitils';
import {
    AnalyticsUserEvent,
    PersistedUserEvent,
    type PersistedUserEventSmsConsentMetadata,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useQueryClient } from '@tanstack/react-query';
import addDays from 'date-fns/addDays';
import differenceInDays from 'date-fns/differenceInDays';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { Platform } from 'react-native';
import {
    AnimatePresence,
    Animations,
    ArrowLeftIcon,
    ArrowRightIcon,
    BadgeLabel,
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Checkbox,
    ClockIcon,
    CONTENT_MAX_WIDTH,
    FootnoteRegular,
    FrostedSurface,
    H5Regular,
    H6Regular,
    IconButton,
    Image,
    isNonMobileWeb,
    LabelRegular,
    ScrollView,
    Shadows,
    Sheet,
    Spinner,
    UpcomingSessionWidget,
    useMedia,
    useToastController,
    VideoIcon,
    XStack,
    YStack,
} from 'smoothie';

import { useSendEmailToSupport } from '../../common/hooks/useSendEmailToSupport';
import { isEmpty } from '../../common/misc';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import useGetUserEvents from '../../events/hooks/useGetUserEvents';
import { useLogUserEvent } from '../../events/hooks/useLogUserEvent';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { GuideAvailabilityCard } from '../../guidance/components/GuideAvailabilityCard';
import {
    GuideDetailsSheet,
    guideForDetailsModalAtom,
    isGuideDetailsModalOpenAtom,
} from '../../guidance/components/GuideDetailsModal';
import type { Guide, GuideWithAvailability } from '../../guidance/types/Guide';
import { useBookAvailableGuide } from '../../guidanceSessions/hooks/useBookAvailableGuide';
import { useMembershipGuidanceSessionInfo } from '../../guidanceSessions/hooks/useMembershipGuidanceSessionInfo';
import {
    DEFAULT_SESSION_TIME_ZONE_LABEL,
    GuidanceSessionType,
} from '../../guidanceSessions/types/GuidanceSessions';
import { captureException, track, trackView } from '../../logger/Logger';
import { useUpdateMemberJourneyLog } from '../../member-profile/hooks/useUpdateMemberJourneyLog';
import { userHasSmsConsentForCurrentPhoneNumber } from '../../notifications/helpers';
import {
    SMS_OPT_IN_DISCLOSURE_HEADLINE,
    SMS_OPT_IN_DISCLOSURE_TEXT,
} from '../../notifications/smsConsentDisclosure';
import { useGuidesWithFirstSessionAvailabilityData } from '../hooks/useGuidesWithFirstSessionAvailabilityData';
import { type BookFurtherOutOption } from '../types/BookFurtherOut';
import {
    type Direction,
    getNonEmptyAvailabilityPrevOrNext,
} from '../utils/bookAvailableGuides';
import OnboardingFullScreenView from './OnboardingFullScreenView';
import { WhyBookFurtherOutSheet } from './WhyBookFurtherOutSheet';

const FIRST_SESSION_TYPE = GuidanceSessionType.DISCOVERY;
const FIRST_SESSION_DURATION_MINUTES = 45;

const DISPLAY_IN_CLIENT_TIME_ZONE = true;

/** After this many successful forward day navigations, the next forward press opens the friction sheet first. */
const NEXT_PRESS_COUNT_SHEET_THRESHOLD = 7;

const DayNavigator = ({
    selectedDayLabel,
    isOnEarliestDayWithAvailability,
    isOnLatestDayWithAvailability,
    isAwaitingNextData,
    displayInClientTimeZone = true,
    onPressPrev,
    onPressNext,
}: {
    selectedDayLabel: string;
    isOnEarliestDayWithAvailability: boolean;
    isOnLatestDayWithAvailability: boolean;
    isAwaitingNextData: boolean;
    displayInClientTimeZone?: boolean;
    onPressPrev: () => void;
    onPressNext: () => void;
}) => {
    const timeZoneLabel = useMemo(
        () =>
            displayInClientTimeZone
                ? getClientTimeZoneLabel()
                : DEFAULT_SESSION_TIME_ZONE_LABEL,
        [displayInClientTimeZone],
    );

    useEffect(() => {
        if (isOnLatestDayWithAvailability) {
            track(
                AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_REACHED_LAST_DAY,
            );
        }
    }, [isOnLatestDayWithAvailability]);

    return (
        <XStack
            width="100%"
            backgroundColor="$white"
            borderRadius={24}
            padding={16}
            $sm={{ padding: 24 }}
            alignItems="center"
            justifyContent="space-between"
            gap={12}
            {...Shadows.MEDIUM}
        >
            <IconButton
                variant="secondary"
                icon={ArrowLeftIcon}
                disabled={isOnEarliestDayWithAvailability}
                onPress={onPressPrev}
                size="tiny"
            />
            <YStack
                gap={8}
                $sm={{ gap: 2 }}
                justifyContent="center"
                alignItems="center"
            >
                {isNonMobileWeb ? (
                    <BodyLargeEmphasized textAlign="center">
                        {selectedDayLabel}
                    </BodyLargeEmphasized>
                ) : (
                    <BodyMediumEmphasized lineHeight={16} textAlign="center">
                        {selectedDayLabel}
                    </BodyMediumEmphasized>
                )}
                <LabelRegular
                    textAlign="center"
                    lineHeight={12}
                    $sm={{ lineHeight: 16 }}
                    color="$gray1000"
                >
                    {`Sessions shown in ${timeZoneLabel}`}
                </LabelRegular>
            </YStack>
            {isAwaitingNextData ? (
                <Spinner width={32} height={32} overflow="visible" />
            ) : (
                <IconButton
                    variant="secondary"
                    icon={ArrowRightIcon}
                    disabled={isOnLatestDayWithAvailability}
                    onPress={onPressNext}
                    size="tiny"
                />
            )}
        </XStack>
    );
};

const NoGuidesAvailableCard = () => (
    <FrostedSurface
        borderRadius={24}
        padding={24}
        gap={8}
        alignItems="center"
        justifyContent="center"
        width="100%"
    >
        <H6Regular textAlign="center" color="$gray1200">
            No Guides available
        </H6Regular>
        <BodyMediumRegular textAlign="center" color="$gray1200">
            It looks like our Guides are fully booked on this day.
        </BodyMediumRegular>
    </FrostedSurface>
);

const HelperMessage = ({
    title,
    subtitle,
}: {
    title?: string;
    subtitle?: string;
}) => {
    return (
        <AnimatePresence>
            {title != null && (
                <YStack
                    key={title}
                    backgroundColor="$green1100"
                    borderRadius={24}
                    paddingVertical={12}
                    paddingHorizontal={24}
                    alignItems="center"
                    gap={4}
                    alignSelf="center"
                    maxWidth={350}
                    width="100%"
                    {...Shadows.MEDIUM}
                    {...Animations.FadeIn}
                    exitStyle={{ opacity: 0 }}
                >
                    <BodyMediumEmphasized color="$green100" textAlign="center">
                        {title}
                    </BodyMediumEmphasized>
                    <BodySmallRegular
                        color="$green100"
                        textAlign="center"
                        alignSelf="center"
                        maxWidth={275}
                    >
                        {subtitle}
                    </BodySmallRegular>
                </YStack>
            )}
        </AnimatePresence>
    );
};

const MembershipGuideAvailabilitySection = ({
    membershipGuide,
    membershipGuideAvailability,
    otherGuidesHaveAvailability,
    isOnEarliestDayWithAvailability,
    isToday,
    nextButtonPressCount,
    displayInClientTimeZone = true,
    onPressDetails,
    onPressTime,
}: {
    membershipGuide: GuideWithAvailability;
    membershipGuideAvailability: Date[];
    otherGuidesHaveAvailability: boolean;
    isOnEarliestDayWithAvailability: boolean;
    isToday: boolean;
    nextButtonPressCount: number;
    displayInClientTimeZone?: boolean;
    onPressDetails: () => void;
    onPressTime: (guide: GuideWithAvailability, time: Date) => void;
}) => {
    const hasMembershipGuideSlots = !isEmpty(membershipGuideAvailability);

    const helperMessage = useMemo(() => {
        if (isOnEarliestDayWithAvailability && hasMembershipGuideSlots) {
            return {
                title: "You're in luck!",
                subtitle: isToday
                    ? `${membershipGuide.firstName} has availability today.`
                    : `${membershipGuide.firstName} has availability on this day.`,
            };
        }

        if (
            !hasMembershipGuideSlots &&
            otherGuidesHaveAvailability &&
            nextButtonPressCount < 4
        ) {
            return {
                title: isToday
                    ? `Looks like ${membershipGuide.firstName} is busy today`
                    : `Looks like ${membershipGuide.firstName} is busy on this day`,
                subtitle:
                    'Check out other Fruitful Guides with availability below.',
            };
        }

        if (
            nextButtonPressCount > NEXT_PRESS_COUNT_SHEET_THRESHOLD - 2 &&
            nextButtonPressCount <= NEXT_PRESS_COUNT_SHEET_THRESHOLD
        ) {
            return {
                title: "Can't find an earlier time?",
                subtitle:
                    'We recommend trying to meet within the first few days of signing up.',
            };
        }

        return null;
    }, [
        isOnEarliestDayWithAvailability,
        isToday,
        membershipGuide.firstName,
        hasMembershipGuideSlots,
        otherGuidesHaveAvailability,
        nextButtonPressCount,
    ]);

    return (
        <YStack gap={12}>
            <HelperMessage
                key={helperMessage?.title}
                title={helperMessage?.title}
                subtitle={helperMessage?.subtitle}
            />
            <GuideAvailabilityCard
                guide={membershipGuide}
                availability={membershipGuideAvailability}
                displayInClientTimeZone={displayInClientTimeZone}
                onPressDetails={onPressDetails}
                onPressTime={onPressTime}
            />
        </YStack>
    );
};

const SessionConfirmationSheet = ({
    isOpen,
    selectedGuide,
    selectedTime,
    membershipGuide,
    onClose,
    onConfirmAndBookSuccess,
}: {
    isOpen: boolean;
    selectedGuide: Guide | null;
    selectedTime: Date | null;
    membershipGuide: Guide | null;
    onClose: () => void;
    onConfirmAndBookSuccess: () => void;
}) => {
    const toast = useToastController();
    const { getSessionDetails } = useMembershipGuidanceSessionInfo();
    const { bookAvailableGuide, isBooking } = useBookAvailableGuide();
    const user = useAuthedUser();
    const { logUserEventAsync, isLoggingUserEvent: isLoggingSMSConsent } =
        useLogUserEvent();
    const { data: smsConsentUserEvents } = useGetUserEvents({
        eventType: PersistedUserEvent.SMS_CONSENT_OPT_IN,
    });
    const [smsConsentChecked, setSmsConsentChecked] = useState(false);
    const showSmsConsent = !userHasSmsConsentForCurrentPhoneNumber(
        smsConsentUserEvents,
        user,
    );
    const isProcessingBooking = isBooking || isLoggingSMSConsent;

    const isMembershipGuide = useMemo(
        () => selectedGuide?.id === membershipGuide?.id,
        [selectedGuide, membershipGuide],
    );

    useEffect(() => {
        if (isOpen && selectedGuide != null && selectedTime != null) {
            track(
                AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_CONFIRM_SHEET,
                {
                    isMembershipGuide,
                },
            );
        }
    }, [selectedGuide, isMembershipGuide, isOpen, selectedTime]);

    const onCloseWithoutBooking = useCallback(() => {
        track(AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_DISMISS_CONFIRM);
        onClose();
    }, [onClose]);

    const handleConfirmAndBook = useCallback(async () => {
        if (selectedGuide == null || selectedTime == null) {
            return;
        }
        track(AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_CONFIRM_PRESSED, {
            daysFromNow: differenceInDays(selectedTime, new Date()),
            guideSwap: isMembershipGuide
                ? 'false'
                : `${membershipGuide?.signUpId ?? ''} -> ${selectedGuide.signUpId ?? ''}`,
        });
        try {
            if (showSmsConsent && smsConsentChecked) {
                if (user.phoneNumber == null || user.phoneNumber === '') {
                    throw new Error(
                        'A phone number is required to enable SMS reminders.',
                    );
                }
                const metadata: PersistedUserEventSmsConsentMetadata = {
                    phoneNumber: user.phoneNumber,
                    disclosureText: `${SMS_OPT_IN_DISCLOSURE_HEADLINE} ${SMS_OPT_IN_DISCLOSURE_TEXT}`,
                    platform: Platform.OS,
                };
                await logUserEventAsync(
                    PersistedUserEvent.SMS_CONSENT_OPT_IN,
                    metadata,
                );
            }
            await bookAvailableGuide({
                selectedGuide,
                membershipGuideId: membershipGuide?.id,
                sessionType: FIRST_SESSION_TYPE,
                startTimeUtcDate: selectedTime,
            });
            onConfirmAndBookSuccess();
            onClose();
        } catch (e) {
            captureException(e);
            toast.show(
                getErrorMessage(e) ??
                    'Could not book your session. Please try again.',
                { duration: 4000 },
            );
        }
    }, [
        selectedGuide,
        selectedTime,
        membershipGuide,
        bookAvailableGuide,
        onConfirmAndBookSuccess,
        onClose,
        toast,
        showSmsConsent,
        smsConsentChecked,
        user,
        logUserEventAsync,
    ]);

    if (selectedGuide == null || selectedTime == null) {
        return null;
    }

    const displayName = [selectedGuide.firstName, selectedGuide.lastName]
        .filter(Boolean)
        .join(' ');

    const sessionEnd = new Date(
        selectedTime.getTime() + FIRST_SESSION_DURATION_MINUTES * 60 * 1000,
    );

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onCloseWithoutBooking}
            title="Confirm your session"
            showCloseButton={!isProcessingBooking}
            dismissOnOverlayPress={!isProcessingBooking}
            disableDrag={true}
        >
            <YStack gap={16}>
                <YStack
                    backgroundColor="$gray200"
                    borderRadius={24}
                    padding={16}
                    gap={16}
                >
                    <XStack alignItems="center" gap={16}>
                        <Image
                            source={{
                                uri: selectedGuide.profileImageUrl ?? '',
                            }}
                            width={72}
                            height={72}
                            borderRadius={72}
                        />
                        <YStack gap={4}>
                            <BodyLargeEmphasized>
                                {displayName}
                            </BodyLargeEmphasized>
                            <FootnoteRegular color="$gray1000">
                                CFP&reg;
                            </FootnoteRegular>
                        </YStack>
                    </XStack>
                    <BodyMediumRegular>
                        {`You'll meet with ${selectedGuide.firstName} to refine your Money Map, finalize your goals, and get your system live.`}
                    </BodyMediumRegular>
                </YStack>

                <UpcomingSessionWidget
                    title={getSessionDetails(FIRST_SESSION_TYPE).title}
                    session={{
                        startTime: selectedTime.toISOString(),
                        endTime: sessionEnd.toISOString(),
                        videoConferenceUrl: '',
                    }}
                    guide={{ firstName: selectedGuide.firstName }}
                    variant="row"
                />

                {showSmsConsent && (
                    <XStack
                        alignItems="flex-start"
                        gap={10}
                        paddingHorizontal={4}
                    >
                        <Checkbox
                            id="sms-consent"
                            checked={smsConsentChecked}
                            onCheckedChange={(checked) => {
                                setSmsConsentChecked(checked === true);
                            }}
                            disabled={isProcessingBooking}
                            marginTop={-1}
                        />
                        <YStack flex={1} gap={6}>
                            <BodySmallRegular
                                cursor="pointer"
                                onPress={() => {
                                    if (!isProcessingBooking) {
                                        setSmsConsentChecked((prev) => !prev);
                                    }
                                }}
                            >
                                {SMS_OPT_IN_DISCLOSURE_HEADLINE}
                            </BodySmallRegular>
                            <BodySmallRegular color="$gray1000">
                                {SMS_OPT_IN_DISCLOSURE_TEXT}
                            </BodySmallRegular>
                        </YStack>
                    </XStack>
                )}

                <XStack width="100%" paddingTop={16}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        text={isProcessingBooking ? '' : 'Confirm and book'}
                        icon={isProcessingBooking ? <Spinner /> : undefined}
                        disabled={isProcessingBooking}
                        onPress={() => {
                            void handleConfirmAndBook();
                        }}
                    />
                </XStack>
            </YStack>
        </Sheet>
    );
};

const HelpSheet = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const sendEmailToSupport = useSendEmailToSupport();

    useEffect(() => {
        if (isOpen) {
            trackView('Onboarding/BookAvailableGuides/HelpSheet');
        }
    }, [isOpen]);

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="No availability"
            showCloseButton={true}
            dismissOnOverlayPress={false}
            disableDrag={true}
        >
            <YStack gap={32}>
                <BodyMediumRegular textAlign="center" color="$gray1200">
                    We couldn&apos;t find any available Guidance sessions now.
                    Please contact our Hospitality team for help with booking
                    your first session.
                </BodyMediumRegular>
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Contact Fruitful Hospitality"
                    onPress={() => {
                        void sendEmailToSupport({
                            subject: 'First session booking help',
                        });
                    }}
                />
            </YStack>
        </Sheet>
    );
};

const OnboardingBookAvailableGuides = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('Onboarding/BookAvailableGuides');

    const user = useAuthedUser();
    const membershipGuide = user.membership?.guide;
    const membershipGuideId = membershipGuide?.id;
    const membershipId = user.membership?.id ?? '';

    const { value: isGoyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);

    const initDate = useMemo(() => new Date(), []);
    const [selectedDay, setSelectedDay] = useState<Date>(initDate);

    const {
        dateRange1,
        dateRange2,
        availabilityQueryChunk1,
        availabilityQueryChunk2,
        availabilityQueryChunk3,
        allGuidesWithAvailability,
        earliestDayWithAvailability,
        isOnEarliestDayWithAvailability,
        isOnLatestDayWithAvailability,
        hasNoAvailabilityAcrossRanges,
        totalDaysInRanges,
        maxNavigableDay,
        minNavigableDay,
    } = useGuidesWithFirstSessionAvailabilityData({
        initDate,
        selectedDay,
        membershipId,
        membershipGuideId,
    });
    const { updateMemberJourneyLog } = useUpdateMemberJourneyLog();
    const queryClient = useQueryClient();

    const [
        selectedDayAvailabilityOtherGuides,
        setSelectedDayAvailabilityOtherGuides,
    ] = useState<Array<GuideWithAvailability | undefined> | undefined>(
        undefined, // init undefined so we can distinguish component init from empty results
    );
    const [
        selectedDayAvailabilityMembershipGuide,
        setSelectedDayAvailabilityMembershipGuide,
    ] = useState<GuideWithAvailability | undefined>(undefined);

    const [isGuideDetailsSheetOpen, setShowGuideDetailsSheet] = useState(false);
    const [isConfirmationSheetOpen, setIsConfirmationSheetOpen] =
        useState(false);
    const [selectedGuideForConfirmation, setSelectedGuideForConfirmation] =
        useState<Guide | null>(null);
    const [selectedTimeForConfirmation, setSelectedTimeForConfirmation] =
        useState<Date | null>(null);
    const [nextButtonPressCount, setNextButtonPressCount] = useState(0);
    const [isBookFurtherOutSheetOpen, setIsBookFurtherOutSheetOpen] =
        useState(false);
    const [isHelpSheetOpen, setIsHelpSheetOpen] = useState(false);
    const hasAnsweredBookFurtherOutSheet = useRef(false);
    const hasShownHelpSheet = useRef(false);
    const scrollToBottomTrackedRef = useRef(false);
    const setShowGuideDetailsModal = useSetAtom(isGuideDetailsModalOpenAtom);
    const setGuideForDetailsModal = useSetAtom(guideForDetailsModalAtom);
    const breakpoints = useMedia();

    const tomorrowDate = useMemo(() => addDays(initDate, 1), []);
    const isToday = useMemo(
        () => isSameDay(selectedDay, initDate),
        [selectedDay, initDate],
    );
    const isTomorrow = useMemo(
        () => isSameDay(selectedDay, tomorrowDate),
        [selectedDay, tomorrowDate],
    );
    const selectedDayLabel = useMemo(() => {
        if (isToday) {
            return 'Today';
        } else if (isTomorrow) {
            return 'Tomorrow';
        } else {
            return formatDayOfWeekMonthDay(selectedDay);
        }
    }, [isToday, isTomorrow, selectedDay]);

    useEffect(() => {
        if (!hasShownHelpSheet.current && hasNoAvailabilityAcrossRanges) {
            setIsHelpSheetOpen(true);
            hasShownHelpSheet.current = true;
        }
    }, [hasNoAvailabilityAcrossRanges]);

    const handleDayNavigation = useCallback(
        (
            selectedDay: Date,
            direction: Direction,
            checkSelectedDay: boolean = false,
        ): boolean => {
            if (isEmpty(allGuidesWithAvailability)) {
                return false;
            }

            const availabilityForDay = getNonEmptyAvailabilityPrevOrNext({
                guides: allGuidesWithAvailability,
                selectedDay,
                direction,
                maxIterations: totalDaysInRanges,
                checkSelectedDay,
                stopAtDay:
                    direction === 'next' ? maxNavigableDay : minNavigableDay,
            });
            const hasAvailabilityForDay =
                availabilityForDay.availability.length > 0;
            if (!hasAvailabilityForDay) {
                return false;
            }
            setSelectedDay(availabilityForDay.day);
            setSelectedDayAvailabilityMembershipGuide(
                availabilityForDay.availability.find(
                    (g) => g?.id === membershipGuideId,
                ),
            );
            setSelectedDayAvailabilityOtherGuides(
                availabilityForDay.availability.filter(
                    (g) => g?.id !== membershipGuideId,
                ),
            );
            return true;
        },
        [
            allGuidesWithAvailability,
            membershipGuideId,
            maxNavigableDay,
            minNavigableDay,
            totalDaysInRanges,
        ],
    );

    const handlePressPrevDay = useCallback(() => {
        track(AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_DAY_NAV_PREV);
        handleDayNavigation(selectedDay, 'prev');
        setNextButtonPressCount((c) => Math.max(c - 1, 0));
    }, [selectedDay, handleDayNavigation]);
    const handlePressNextDay = useCallback(() => {
        if (
            nextButtonPressCount === NEXT_PRESS_COUNT_SHEET_THRESHOLD &&
            !hasAnsweredBookFurtherOutSheet.current
        ) {
            setIsBookFurtherOutSheetOpen(true);
            return;
        }
        track(AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_DAY_NAV_NEXT);
        const didNavigate = handleDayNavigation(selectedDay, 'next');
        if (didNavigate) {
            setNextButtonPressCount((c) => c + 1);
        }
    }, [nextButtonPressCount, handleDayNavigation, selectedDay]);

    const onBookFurtherOutSaveAndContinue = useCallback(
        (option: BookFurtherOutOption) => {
            if (option != null) {
                hasAnsweredBookFurtherOutSheet.current = true;
            }
            const didNavigate = handleDayNavigation(selectedDay, 'next');
            if (didNavigate) {
                setNextButtonPressCount((c) => c + 1);
            }
        },
        [handleDayNavigation, selectedDay],
    );

    const handleGuideDetailsPress = useCallback(
        (guide: Guide) => {
            track(
                AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_VIEW_GUIDE_DETAILS,
                {
                    isMembershipGuide: guide.id === membershipGuideId,
                },
            );
            setGuideForDetailsModal(guide);
            if (breakpoints.sm) {
                setShowGuideDetailsModal(true);
            } else {
                setShowGuideDetailsSheet(true);
            }
        },
        [breakpoints.sm, membershipGuideId],
    );

    const handleGuideTimePress = useCallback((guide: Guide, time: Date) => {
        setSelectedGuideForConfirmation(guide);
        setSelectedTimeForConfirmation(time);
        setIsConfirmationSheetOpen(true);
    }, []);

    const onConfirmAndBookSuccess = useCallback(() => {
        setSelectedGuideForConfirmation(null);
        setSelectedTimeForConfirmation(null);
        void updateMemberJourneyLog({
            completedGatedOnboarding: true,
        });
        void queryClient.invalidateQueries({
            queryKey: ['getMessagingAccount'],
        });
        router.replace('/home');
    }, [updateMemberJourneyLog, queryClient]);

    const handleMainScroll = useCallback(
        (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            if (scrollToBottomTrackedRef.current) {
                return;
            }
            const { contentOffset, contentSize, layoutMeasurement } =
                event.nativeEvent;
            const paddingToBottom = 200;
            const isCloseEnoughToBottom =
                layoutMeasurement.height + contentOffset.y >=
                contentSize.height - paddingToBottom;
            if (isCloseEnoughToBottom) {
                scrollToBottomTrackedRef.current = true;
                track(
                    AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_SCROLLED_ALL_GUIDES,
                );
            }
        },
        [],
    );

    // Initialize state after data loads
    useEffect(() => {
        if (
            selectedDayAvailabilityOtherGuides == null &&
            selectedDayAvailabilityMembershipGuide == null &&
            availabilityQueryChunk1.guidesWithAvailability != null
        ) {
            handleDayNavigation(
                selectedDay,
                'next',
                /* checkSelectedDay */ true,
            );
        }
    }, [
        selectedDayAvailabilityOtherGuides,
        selectedDayAvailabilityMembershipGuide,
        selectedDay,
        availabilityQueryChunk1.guidesWithAvailability,
        handleDayNavigation,
    ]);
    useEffect(() => {
        // When earliestDayWithAvailability updates, if that day is ahead of selectedDay, then skip to that day
        if (isBefore(selectedDay, earliestDayWithAvailability)) {
            handleDayNavigation(
                earliestDayWithAvailability,
                'next',
                /* checkSelectedDay */ true,
            );
        }
    }, [selectedDay, earliestDayWithAvailability, handleDayNavigation]);

    const otherGuidesText = isToday
        ? 'Other Guides with availability today'
        : 'Other Guides with availability on this day';

    const selectedDayHasOtherGuides =
        (selectedDayAvailabilityOtherGuides ?? []).length > 0;

    return (
        <OnboardingFullScreenView
            hasBackButton={isGoyoEnabled}
            webSafariBarTint
        >
            <ScrollView
                scrollEventThrottle={400}
                onScroll={handleMainScroll}
                contentContainerStyle={{
                    flexGrow: 1,
                    flexDirection: 'column',
                    gap: 32,
                    alignItems: 'center',
                    paddingBottom: 37,
                    paddingTop: 24,
                    maxWidth: CONTENT_MAX_WIDTH,
                    width: '100%',
                    alignSelf: 'center',
                }}
            >
                <YStack gap={16} alignItems="center">
                    <H5Regular textAlign="center">
                        Book your first session
                    </H5Regular>

                    <XStack
                        gap={16}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <BadgeLabel
                            variant="neutral"
                            text={`${FIRST_SESSION_DURATION_MINUTES} minutes`}
                            icon={ClockIcon}
                            size="regular"
                        />
                        <BadgeLabel
                            variant="neutral"
                            text="Zoom meeting"
                            icon={VideoIcon}
                            size="regular"
                        />
                    </XStack>
                </YStack>

                {availabilityQueryChunk1.isLoading ? (
                    <XStack justifyContent="center">
                        <Spinner marginVertical={32} />
                    </XStack>
                ) : (
                    <YStack gap={24} width="100%">
                        <DayNavigator
                            selectedDayLabel={selectedDayLabel}
                            isOnEarliestDayWithAvailability={
                                isOnEarliestDayWithAvailability
                            }
                            isOnLatestDayWithAvailability={
                                isOnLatestDayWithAvailability
                            }
                            isAwaitingNextData={
                                (availabilityQueryChunk2.isLoading &&
                                    isSameDay(selectedDay, dateRange1.end)) ||
                                (availabilityQueryChunk3.isLoading &&
                                    isSameDay(selectedDay, dateRange2.end))
                            }
                            displayInClientTimeZone={
                                DISPLAY_IN_CLIENT_TIME_ZONE
                            }
                            onPressPrev={handlePressPrevDay}
                            onPressNext={handlePressNextDay}
                        />

                        {membershipGuide != null && (
                            <MembershipGuideAvailabilitySection
                                membershipGuide={
                                    selectedDayAvailabilityMembershipGuide ?? {
                                        ...membershipGuide,
                                        sessionType: FIRST_SESSION_TYPE,
                                    }
                                }
                                membershipGuideAvailability={
                                    selectedDayAvailabilityMembershipGuide?.sessionAvailableTimes ??
                                    []
                                }
                                otherGuidesHaveAvailability={
                                    selectedDayHasOtherGuides
                                }
                                isOnEarliestDayWithAvailability={
                                    isOnEarliestDayWithAvailability
                                }
                                isToday={isToday}
                                nextButtonPressCount={nextButtonPressCount}
                                displayInClientTimeZone={
                                    DISPLAY_IN_CLIENT_TIME_ZONE
                                }
                                onPressDetails={() => {
                                    handleGuideDetailsPress(membershipGuide);
                                }}
                                onPressTime={(guide, time) => {
                                    handleGuideTimePress(guide, time);
                                }}
                            />
                        )}

                        {selectedDayHasOtherGuides ? (
                            <YStack gap={24}>
                                {membershipGuide != null && (
                                    <BodyMediumEmphasized textAlign="center">
                                        {otherGuidesText}
                                    </BodyMediumEmphasized>
                                )}
                                {(selectedDayAvailabilityOtherGuides ?? []).map(
                                    (guide) => (
                                        <GuideAvailabilityCard
                                            key={guide?.id ?? ''}
                                            guide={guide}
                                            availability={
                                                guide?.sessionAvailableTimes ??
                                                []
                                            }
                                            displayInClientTimeZone={
                                                DISPLAY_IN_CLIENT_TIME_ZONE
                                            }
                                            onPressDetails={
                                                handleGuideDetailsPress
                                            }
                                            onPressTime={handleGuideTimePress}
                                        />
                                    ),
                                )}
                            </YStack>
                        ) : membershipGuide == null ? (
                            <NoGuidesAvailableCard />
                        ) : null}

                        <GuideDetailsSheet
                            isOpen={isGuideDetailsSheetOpen}
                            onOpenChange={setShowGuideDetailsSheet}
                        />
                        <SessionConfirmationSheet
                            isOpen={isConfirmationSheetOpen}
                            selectedGuide={selectedGuideForConfirmation}
                            selectedTime={selectedTimeForConfirmation}
                            membershipGuide={membershipGuide ?? null}
                            onClose={() => {
                                setIsConfirmationSheetOpen(false);
                            }}
                            onConfirmAndBookSuccess={onConfirmAndBookSuccess}
                        />
                        <WhyBookFurtherOutSheet
                            isOpen={isBookFurtherOutSheetOpen}
                            onClose={() => {
                                setIsBookFurtherOutSheetOpen(false);
                            }}
                            onSavedAndContinue={onBookFurtherOutSaveAndContinue}
                            daysThreshold={NEXT_PRESS_COUNT_SHEET_THRESHOLD}
                        />
                        {isHelpSheetOpen && (
                            <HelpSheet
                                isOpen={isHelpSheetOpen}
                                onClose={() => {
                                    setIsHelpSheetOpen(false);
                                }}
                            />
                        )}
                    </YStack>
                )}
            </ScrollView>
        </OnboardingFullScreenView>
    );
};

export default OnboardingBookAvailableGuides;
